import { ROUTES } from "@/routes/Routes";
import {
  requestUploadModelTransactionFile,
  requestUploadModelTransactionFiles,
} from "@/state/transactionModels/TransactionModelEffects";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { requestUploadTransactionFiles } from "@/state/transactions/TransactionEffects";
import { Member } from "@/type/core/offices/offices.types";
import {
  SignatureRequirementMode,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import { FileHelper } from "@/utils/helpers/file-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import ModelPdfContainer from "@components/core/pdf/ModelPdfContainer";
import PdfContainer from "@components/core/pdf/PdfContainer";
import CardSection from "@components/core/section/CardSection";
import {
  File,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { Checkbox } from "antd";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SignatureHelper from "@utils/helpers/signature-helper";
import { toastError } from "@utils/helpers/toast-helper";

interface PositioningProps {
  transactionType: TransactionType;
  files: File[];
  signatories: Signatory[];
  signatures: Signature[];
  signatureRequirementMode: SignatureRequirementMode;
  setSignatureRequirementMode: (
    signatureRequirementMode: SignatureRequirementMode,
  ) => void;
  manualPositioning: boolean;
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  isFromModelTransaction?: boolean;
  className?: string;
  members: Member[];
  officeId: string;
}

const Positioning: FunctionComponent<PositioningProps> = ({
  transactionType,
  files,
  signatories,
  signatures,
  signatureRequirementMode,
  setSignatureRequirementMode,
  manualPositioning,
  setManualPositioning,
  isFromModelTransaction = false,
  className = "",
  members,
  officeId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { transactionDetail } = useCurrentTransaction();

  const route = isFromModelTransaction
    ? ROUTES.extranet.menu.offices.transactionModel.list.generate()
    : ROUTES.extranet.transactions.details.generate(
        transactionDetail?.id ?? "",
      );

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  useEffect(() => {
    return requestUploadTransactionFiles.done.watch(({ result }) => {
      if (result.ok && transactionDetail?.id) {
        navigate(route);
      } else {
        if (result.data && result.data.details) {
          toastError(
            `Une erreur est survenue : ${result.data.code ? `[${result.data.code}]` : ""} ${result.data.details ? result.data.details : ""}`,
          );
        } else {
          toastError(
            `Une erreur est survenue : ${result.errorCode ? `[${result.errorCode}]` : ""} ${result.errorMessage ? result.errorMessage : ""}`,
          );
        }
      }
      setButtonLoading(false);
    });
  });

  useEffect(() => {
    return requestUploadModelTransactionFile.done.watch(({ result }) => {
      if (result.ok) {
        navigate(route);
      }
      setButtonLoading(false);
    });
  });

  useEffect(() => {
    return requestUploadModelTransactionFiles.done.watch(({ result }) => {
      if (result.ok) {
        navigate(route);
      }
      setButtonLoading(false);
    });
  });

  const displayModeCheckbox =
    files.length > 1 &&
    transactionType === "signature" &&
    signatories.length > 1;

  const areSignaturesValid = (): boolean => {
    if (signatureRequirementMode === "SOME") {
      const presentSignatories = new Set();
      const presentDocuments = new Set();
      let maxRank = 0;
      for (const signature of signatures) {
        if (signature && signature.isMoved) {
          presentSignatories.add(signature.email);
          presentDocuments.add(signature.rank);
        }
        if (signature && signature.rank && signature.rank > maxRank) {
          maxRank = signature.rank;
        }
      }

      return (
        signatories.length === presentSignatories.size &&
        presentDocuments.size === maxRank
      );
    } else {
      return (
        signatures &&
        SignatureHelper.didSignatoriesSignAtLeastOnceOnEveryFile(
          signatures,
          signatories,
          files,
        )
      );
    }
  };

  const isFinishButtonDisabled = () => {
    const names: (string | undefined)[] = signatures
      .filter((signature) => signature.rank === 1)
      .map((signature) => signature.name);
    const hasDuplicatedName = new Set(names).size !== names.length;
    return (
      (!isFromModelTransaction &&
        transactionType === "signature" &&
        !areSignaturesValid()) ||
      (!isFromModelTransaction &&
        transactionType === "scellement" &&
        signatures[0] &&
        !signatures[0].isMoved) ||
      (isFromModelTransaction &&
        transactionType === "signature" &&
        (names.some((name) => !name) || hasDuplicatedName))
    );
  };

  const handleSubmit = () => {
    setButtonLoading(true);
    const body = FileHelper.createUploadFilesFormData(
      {
        transactionType,
        files,
        signatories,
        signatures,
        signatureRequirementMode,
      },
      isFromModelTransaction,
    );

    if (isFromModelTransaction) {
      if (files.length === 1) {
        void requestUploadModelTransactionFile({
          dto: body,
          id: transactionDetail?.id,
        });
      } else {
        void requestUploadModelTransactionFiles({
          dto: body,
          id: transactionDetail?.id,
        });
      }
    } else {
      void requestUploadTransactionFiles({
        dto: body,
        id: transactionDetail?.id,
      });
    }

    return null;
  };

  useEffect(() => {
    if (transactionType === "scellement" && signatures[0]) {
      signatures[0].order = 1;
      signatures[0].name = "scellement";
    }
  }, []);

  return (
    <CardSection
      title={
        isFromModelTransaction && transactionType === "signature"
          ? t("signature.positioning.title.signatureModel")
          : t(`signature.positioning.title.${transactionType}`)
      }
      className={`app-positioning ${className}`}
    >
      <>
        {displayModeCheckbox && (
          <Checkbox
            id="signatureRequirementMode_checkbox"
            checked={signatureRequirementMode === "ALL"}
            onChange={(e) => {
              if (!e.target.checked) {
                setSignatureRequirementMode("SOME");
              } else {
                setSignatureRequirementMode("ALL");
              }
            }}
          >
            {t("signature.positioning.signatureRequirementMode.label")}
            <InfoModal value="signatureRequirementMode" info />
          </Checkbox>
        )}
        {transactionType === "signature" &&
          files.map((file: File, index: number) => {
            return isFromModelTransaction ? (
              <ModelPdfContainer
                key={"document_" + (+index + +1)}
                index={index + 1}
                file={file}
                numberOfFiles={files.length}
                signatories={signatories}
                signatures={signatures}
                transactionType={transactionType}
                buttonLoading={buttonLoading}
                setButtonLoading={setButtonLoading}
                manualPositioning={manualPositioning}
                setManualPositioning={setManualPositioning}
              />
            ) : (
              <PdfContainer
                key={"document_" + (+index + +1)}
                index={index + 1}
                file={file}
                numberOfFiles={files.length}
                signatories={signatories}
                signatures={signatures}
                transactionType={transactionType}
                buttonLoading={buttonLoading}
                setButtonLoading={setButtonLoading}
                manualPositioning={manualPositioning}
                setManualPositioning={setManualPositioning}
                showSeparator={index > 0 || displayModeCheckbox}
                members={members}
                officeId={officeId}
              />
            );
          })}
        {transactionType === "scellement" &&
          (isFromModelTransaction ? (
            <ModelPdfContainer
              index={1}
              file={files[0]}
              numberOfFiles={files.length}
              signatories={signatories}
              signatures={signatures}
              transactionType={transactionType}
              manualPositioning={manualPositioning}
              setManualPositioning={setManualPositioning}
            />
          ) : (
            <PdfContainer
              index={1}
              file={files[0]}
              numberOfFiles={files.length}
              signatories={signatories}
              signatures={signatures}
              transactionType={transactionType}
              manualPositioning={manualPositioning}
              setManualPositioning={setManualPositioning}
              members={members}
              officeId={officeId}
            />
          ))}
        <div className="d-flex align-items-center justify-content-center mb-8">
          <BasicButton
            disabled={isFinishButtonDisabled()}
            isLoading={buttonLoading}
            text={t("buttons.finish")}
            className="btn-finish-positioning"
            onClick={handleSubmit}
          />
        </div>
        {/* TODO: MODAL -> positioning */}
        {/* TODO: MODAL -> confirm combined signature */}
        {/* TODO: MODAL -> disable combined signature */}
      </>
    </CardSection>
  );
};

export default Positioning;
